import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { propertiesService } from '../services/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { resolveImageUrl } from '@/lib/utils';
import { ArrowLeft, Building2, Home, Image as ImageIcon } from 'lucide-react';

function PropertyFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    title: '',
    price: '',
    currency: 'MXN',
    lat: '',
    lng: '',
    address: '',
    bedrooms: '',
    bathrooms: '',
    area: '',
    description: '',
    property_type: 'casa',
  });
  const [loading, setLoading] = useState(false);
  const [fetchingEdit, setFetchingEdit] = useState(isEdit);
  const [error, setError] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (!isEdit || !id) return;
    api.get(`/properties/${id}`)
      .then((r) => {
        const p = r.data;
        setForm({
          title: p.title ?? '',
          price: String(p.price ?? ''),
          currency: p.currency ?? 'MXN',
          lat: String(p.lat ?? ''),
          lng: String(p.lng ?? ''),
          address: p.address ?? '',
          bedrooms: String(p.bedrooms ?? ''),
          bathrooms: String(p.bathrooms ?? ''),
          area: String(p.area ?? ''),
          description: p.description ?? '',
          property_type: p.property_type ?? 'casa',
        });
        setImages(p.images ?? []);
      })
      .catch(() => setError('No se pudo cargar la propiedad.'))
      .finally(() => setFetchingEdit(false));
  }, [id, isEdit]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const payload = {
      title: form.title,
      price: Number(form.price),
      currency: form.currency,
      lat: Number(form.lat),
      lng: Number(form.lng),
      address: form.address,
      bedrooms: Number(form.bedrooms) || 0,
      bathrooms: Number(form.bathrooms) || 0,
      area: Number(form.area) || 0,
      description: form.description || null,
      property_type: form.property_type,
    };
    try {
      if (isEdit) {
        await api.put(`/properties/${id}`, payload);
        navigate('/admin/properties');
      } else {
        const resp = await api.post('/properties', payload);
        navigate(`/admin/properties/${resp.data.id}/edit`);
      }
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Error al guardar la propiedad.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/properties')}
            aria-label="Volver"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm">
              {isEdit ? 'Editar propiedad' : 'Nueva propiedad'}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        {fetchingEdit && (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!fetchingEdit && <><div className="mb-8">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
            <Home className="w-5 h-5 text-primary" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isEdit ? 'Editar propiedad' : 'Agregar propiedad'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isEdit ? 'Modifica los datos de la propiedad' : 'Registra una nueva propiedad en tu agencia'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="title">Título</Label>
            <Input id="title" value={form.title} onChange={set('title')} placeholder="Casa en Polanco" required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="price">Precio</Label>
              <Input id="price" type="number" min="0" value={form.price} onChange={set('price')} placeholder="4 500 000" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="currency">Moneda</Label>
              <select
                id="currency"
                value={form.currency}
                onChange={set('currency')}
                className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="MXN">MXN</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="address">Dirección</Label>
            <Input id="address" value={form.address} onChange={set('address')} placeholder="Av. Presidente Masaryk 123, CDMX" required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="property_type">Tipo de propiedad</Label>
            <select
              id="property_type"
              value={form.property_type}
              onChange={set('property_type')}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="casa">Casa</option>
              <option value="departamento">Departamento</option>
              <option value="local">Local comercial</option>
              <option value="terreno">Terreno</option>
              <option value="oficina">Oficina</option>
              <option value="bodega">Bodega</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="lat">Latitud</Label>
              <Input id="lat" type="number" step="any" value={form.lat} onChange={set('lat')} placeholder="19.4326" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lng">Longitud</Label>
              <Input id="lng" type="number" step="any" value={form.lng} onChange={set('lng')} placeholder="-99.1332" required />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="bedrooms">Recámaras</Label>
              <Input id="bedrooms" type="number" min="0" value={form.bedrooms} onChange={set('bedrooms')} placeholder="3" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bathrooms">Baños</Label>
              <Input id="bathrooms" type="number" min="0" value={form.bathrooms} onChange={set('bathrooms')} placeholder="2" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="area">Área (m²)</Label>
              <Input id="area" type="number" min="0" value={form.area} onChange={set('area')} placeholder="180" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Descripción</Label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Describe la propiedad: características, estado, entorno..."
              rows={4}
              className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/properties')}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1 transition-all active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear propiedad'}
            </Button>
          </div>
        </form>

        {isEdit && id && (
          <div className="mt-10">
            <p className="text-xs text-muted-foreground uppercase tracking-widest font-medium mb-4">
              Fotos de la propiedad
            </p>

            {/* Existing images — index 0 is the cover shown on cards */}
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                {images.map((url, idx) => (
                  <div key={idx} className="relative group aspect-video rounded-xl overflow-hidden border border-border bg-muted">
                    <img src={resolveImageUrl(url)} alt="Foto de propiedad" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const result = await propertiesService.deleteImage(id, idx);
                          setImages(result.images);
                        } catch {
                          setUploadError('No se pudo eliminar la imagen.');
                        }
                      }}
                      className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <span className="text-white text-xs font-medium bg-destructive/80 px-2 py-1 rounded-md">Eliminar</span>
                    </button>
                    <button
                      type="button"
                      aria-label="Hacer portada"
                      onClick={async () => {
                        const order = [idx, ...images.map((_, i) => i).filter((i) => i !== idx)];
                        try {
                          const result = await propertiesService.reorderImages(id, order);
                          setImages(result.images);
                        } catch {
                          setUploadError('No se pudo reordenar.');
                        }
                      }}
                      className="absolute top-1 left-1 z-10 text-[10px] bg-white/90 text-foreground px-1.5 py-0.5 rounded-md"
                    >
                      {idx === 0 ? 'Portada' : 'Hacer portada'}
                    </button>
                    {idx > 0 && (
                      <button
                        type="button"
                        aria-label="Mover a la izquierda"
                        onClick={async () => {
                          const order = images.map((_, i) => i);
                          [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
                          try {
                            const result = await propertiesService.reorderImages(id, order);
                            setImages(result.images);
                          } catch {
                            setUploadError('No se pudo reordenar.');
                          }
                        }}
                        className="absolute bottom-1 left-1 z-10 text-xs bg-white/90 text-foreground w-5 h-5 rounded-md leading-none"
                      >
                        ‹
                      </button>
                    )}
                    {idx < images.length - 1 && (
                      <button
                        type="button"
                        aria-label="Mover a la derecha"
                        onClick={async () => {
                          const order = images.map((_, i) => i);
                          [order[idx], order[idx + 1]] = [order[idx + 1], order[idx]];
                          try {
                            const result = await propertiesService.reorderImages(id, order);
                            setImages(result.images);
                          } catch {
                            setUploadError('No se pudo reordenar.');
                          }
                        }}
                        className="absolute bottom-1 right-1 z-10 text-xs bg-white/90 text-foreground w-5 h-5 rounded-md leading-none"
                      >
                        ›
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Upload new images */}
            <label
              className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                uploading
                  ? 'border-primary/30 bg-primary/5'
                  : 'border-border hover:border-primary/40 hover:bg-muted/30'
              }`}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="sr-only"
                disabled={uploading}
                onChange={async (e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (!files.length) return;
                  setUploading(true);
                  setUploadError('');
                  try {
                    let current = images;
                    for (const file of files) {
                      const result = await propertiesService.uploadImage(id, file);
                      current = result.images;
                    }
                    setImages(current);
                  } catch {
                    setUploadError('Error al subir la imagen. Verifica que sea JPG, PNG o WebP y menos de 10 MB.');
                  } finally {
                    setUploading(false);
                    e.target.value = '';
                  }
                }}
              />
              <div className="flex flex-col items-center gap-1.5 pointer-events-none">
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-xs text-muted-foreground">Subiendo...</p>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-6 h-6 text-muted-foreground/50" />
                    <p className="text-xs text-muted-foreground">Haz clic o arrastra imágenes aquí</p>
                    <p className="text-[10px] text-muted-foreground/60">JPG, PNG, WebP · máx 10 MB</p>
                  </>
                )}
              </div>
            </label>
            {uploadError && <p className="text-xs text-destructive mt-2">{uploadError}</p>}
          </div>
        )}
        </>}
      </main>
    </div>
  );
}

export default PropertyFormPage;
